import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CdkDragDrop,
  CdkDrag,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { Survey, SurveyQuestionOption, SurveyQuestion } from '@app/schemas/surveys';
import { Store } from '@ngrx/store';
import { updateSurveyOptions } from '@app/state/actions/surveys.action';
import { SurveyStorageService } from '@app/services/local-storage/survey-storage.service';
import { State } from '@app/state/reducers';
import { DebugModeService } from '@app/services/debug-mode.service';

@Component({
  selector: 'app-question-card',
  imports: [
    CommonModule,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './question-card.component.html',
  styleUrl: './question-card.component.css'
})
export class QuestionCardComponent implements OnChanges {
  survey: Survey | undefined;
  question!: SurveyQuestion | undefined;
  questionOptions: SurveyQuestionOption[] = [];
  questionImage = "";
  currentImage = "";
  questionTypes: string[] = [
    "Sports Reality Show",
    "Medical Research Faculty",
    "State Park",
    "Video Game Developer",
    "Electric Vehicle Company",
    "Cat Cafe"
  ];
  @Input() questNo!: number;
  @Output() updateQuestionEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  readonly dialog = inject(MatDialog);
  private surveyStorageService = inject(SurveyStorageService);
  @ViewChildren('itemElement') itemElements!: QueryList<ElementRef>;
  selectedItemId: string | null = null;
  selectedItemLabel: string | null = null;
  private isMovingItem = false;
  private shiftTabFromItem = false;

  constructor(
    private store: Store<State>,
    public debugModeService: DebugModeService,
  ) {
    this.store.select('survey').subscribe(value => {
      if (value && 'id' in value) {
        this.survey = value as Survey;
      }
    });
    
    // Load saved questions from localStorage on component initialization
    this.loadSavedQuestions();
  }
  
  private loadSavedQuestions() {
    if (!this.surveyStorageService) {
      console.warn('SurveyStorageService not available');
      return;
    }
    
    const savedQuestions = this.surveyStorageService.loadSurveyQuestions();
    console.log('Loading saved questions from localStorage:', savedQuestions);
    if (savedQuestions.length > 0) {
      // Dispatch action to load saved questions into store
      savedQuestions.forEach(savedQuestion => {
        this.store.dispatch(updateSurveyOptions({
          question_id: savedQuestion.id,
          question: savedQuestion,
        }));
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['questNo']) {
      this.changeQuestionData(changes['questNo'].currentValue);
    }
  }

  changeQuestionData(value: number) {
    if (!this.survey || !this.survey.questions || !this.survey.questions[value]) {
      return;
    }
    this.question = this.survey.questions[value];
    
    // First try to load from localStorage directly
    if (this.surveyStorageService) {
      const savedQuestions = this.surveyStorageService.loadSurveyQuestions();
      const savedQuestion = savedQuestions.find((q: SurveyQuestion) => q.id === this.question!.id);
      
      console.log(`Loading question ${this.question!.id}, found saved:`, savedQuestion);
      
      if (savedQuestion && savedQuestion.options) {
        // Use saved options if available
        console.log('Using saved options:', savedQuestion.options);
        this.questionOptions = [...savedQuestion.options];
        this.questionImage = `/assets/images/survey/questions/${this.question.uuid}.png`;
        this.currentImage = this.questionImage;
        return;
      }
    }
    
    // Use default options from survey
    console.log('Using default options:', this.question!.options);
    this.questionOptions = [...this.question!.options];
    
    this.questionImage = `/assets/images/survey/questions/${this.question.uuid}.png`;
    this.currentImage = this.questionImage;
  }

  protected drop(event: CdkDragDrop<SurveyQuestionOption[]>) {
    moveItemInArray(this.questionOptions, event.previousIndex, event.currentIndex);
    this.saveUserOptions();
  }

  saveUserOptions() {
    const updatedData = structuredClone({ ...this.question!, options: this.questionOptions });
    console.log('Saving options for question:', this.question?.id, 'with options:', this.questionOptions);
    
    this.store.dispatch(updateSurveyOptions({
      question_id: this.question?.id!,
      question: updatedData,
    }));
    
    // Save to localStorage after updating store
    this.store.select('surveyQuestions').subscribe(questions => {
      if (this.surveyStorageService) {
        console.log('Saving to localStorage:', questions);
        this.surveyStorageService.saveSurveyQuestions(questions);
      }
    }).unsubscribe();
  }

  onAnswerHover(id: string) {
    this.currentImage = `/assets/images/survey/answers/${id}-full.png`;
  }

  onAnswerLeave() {
    this.currentImage = this.questionImage;
  }

  splitDescription(text: string): { firstSentence: string, remainingText: string } {
    if (!text) {
      return { firstSentence: '', remainingText: '' };
    }
    
    // Find the first sentence (regex for ., !, or ?)
    const sentenceMatch = text.match(/^[^.!?]*[.!?]/);
    
    if (sentenceMatch) {
      const firstSentence = sentenceMatch[0].trim();
      const remainingText = text.substring(firstSentence.length).trim();
      return { firstSentence, remainingText };
    }
    
    // If no sentence ending found, return the whole text as first sentence
    return { firstSentence: text, remainingText: '' };
  }

  onAnswerKeyDown(event: KeyboardEvent, optionId: string) {
    const currentIndex = this.questionOptions.findIndex(e => e.uuid == optionId);
    const isSelected = this.selectedItemId === optionId;
    this.selectedItemLabel = isSelected ? null : this.questionOptions[currentIndex].body;

    switch (event.key) {
      case 'Tab':
        this.selectedItemId = null;
        this.selectedItemLabel = null;
        if (event.shiftKey) {
          this.shiftTabFromItem = true;
        }
        break;

      case ' ':
        event.preventDefault();
        this.selectedItemId = isSelected ? null : optionId;
        this.selectedItemLabel = this.selectedItemId
          ? `${this.questionOptions[currentIndex].body} selected`
          : `${this.questionOptions[currentIndex].body} unselected`;
        break;

      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = Math.min(this.questionOptions.length - 1, currentIndex + 1);
        if (nextIndex !== currentIndex) {
          if (isSelected) {
            this.isMovingItem = true;
            this.moveItem(currentIndex, nextIndex);
            this.onAnswerHover(optionId);
            setTimeout(() => {
              this.setFocus(nextIndex);
              this.isMovingItem = false;
            }, 0);
          } else {
            this.setFocus(nextIndex);
          }
        }
        this.selectedItemLabel = null;
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex = Math.max(0, currentIndex - 1);
        if (prevIndex !== currentIndex) {
          if (isSelected) {
            this.isMovingItem = true;
            this.moveItem(currentIndex, prevIndex);
            this.onAnswerHover(optionId);
            setTimeout(() => {
              this.setFocus(prevIndex);
              this.isMovingItem = false;
            }, 0);
          } else {
            this.setFocus(prevIndex);
          }
        }
        this.selectedItemLabel = null;
        break;
      }

      case 'Escape':
        this.selectedItemId = null;
        this.selectedItemLabel = null;
        break;
    }
  }

  onListKeyDown(event: KeyboardEvent) {
    // Ignore if focus is already on an option
    if ((event.target as HTMLElement).getAttribute('role') === 'option') {
      return;
    }

    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === ' '
    ) {
      event.preventDefault();

      const index = this.selectedItemId
        ? this.questionOptions.findIndex(o => o.uuid === this.selectedItemId)
        : 0;

      this.setFocus(index);
    }
  }

  onListFocus(event: FocusEvent): void {
    // If focus is already inside an option, do nothing
    const active = document.activeElement as HTMLElement;
    if (active?.getAttribute('role') === 'option') {
      return;
    }

    // When Shift+Tab was pressed from inside an item, skip the focus trap and
    // move focus to the previous focusable element before this listbox container.
    if (this.shiftTabFromItem) {
      this.shiftTabFromItem = false;
      const listbox = event.target as HTMLElement;
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      const allFocusable = Array.from(document.querySelectorAll<HTMLElement>(focusableSelectors));
      const listboxIndex = allFocusable.indexOf(listbox);
      if (listboxIndex > 0) {
        allFocusable[listboxIndex - 1].focus();
      }
      return;
    }

    // Focus selected item OR first item
    const index = this.selectedItemId
      ? this.questionOptions.findIndex(o => o.uuid === this.selectedItemId)
      : 0;

    this.setFocus(index);
  }

  onAnswerBlur(optionId: string) {
    if (!this.isMovingItem && this.selectedItemId === optionId) {
      this.selectedItemId = null;
      this.selectedItemLabel = null;
    }
  }

  moveItem(oldIndex: number, newIndex: number): void {
    const movedItem = this.questionOptions[oldIndex];
    this.questionOptions.splice(oldIndex, 1);
    this.questionOptions.splice(newIndex, 0, movedItem);
    this.saveUserOptions();
  }

  setFocus(index: number): void {
    if (!this.itemElements) {
      return;
    }

    const itemList = this.itemElements.toArray();

    // Make all items untabbable
    itemList.forEach(item =>
      item.nativeElement.setAttribute('tabindex', '-1')
    );

    const newItem = itemList[index];
    if (newItem) {
      newItem.nativeElement.setAttribute('tabindex', '0');
      newItem.nativeElement.focus();
    }
  }
}
